<template>
  <section class="faq-block">
    <h2>{{ heading }}</h2>
    <div class="faq-list">
      <details v-for="(item, index) in faqs" :key="item.question" class="faq-item" :open="index === 0">
        <summary><h3>{{ item.question }}</h3></summary>
        <p>{{ item.answer }}</p>
      </details>
    </div>
  </section>
</template>

<script>
import { upsertJsonLd, removeJsonLd } from '@/utils/seo';

// Question-and-answer list that also publishes FAQPage schema, so Google and
// AI search tools can read the same answers visitors see.
export default {
  name: 'FaqList',
  props: {
    heading: { type: String, default: 'Frequently Asked Questions' },
    faqs: { type: Array, required: true },
    schemaId: { type: String, required: true }
  },
  mounted() {
    upsertJsonLd(this.schemaId, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    });
  },
  beforeUnmount() {
    removeJsonLd(this.schemaId);
  }
};
</script>

<style scoped>
.faq-block {
  max-width: 900px;
  margin: 0 auto;
  padding: 30px;
  text-align: center;
}

h2 {
  font-size: clamp(1.35rem, 2.5vw + 0.5rem, 2.2rem);
  color: #f1c40f;
  margin: 0 0 20px;
  padding-bottom: 10px;
  border-bottom: 3px solid #f1c40f;
  display: inline-block;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.faq-list {
  display: grid;
  gap: 12px;
  margin-top: 10px;
  text-align: left;
}

.faq-item {
  background: rgba(255, 255, 255, 0.06);
  border-left: 4px solid #f1c40f;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
}

summary::-webkit-details-marker {
  display: none;
}

summary::after {
  content: '+';
  flex-shrink: 0;
  color: #f1c40f;
  font-size: 1.6rem;
  line-height: 1;
  font-weight: 700;
}

.faq-item[open] summary::after {
  content: '\2212';
}

summary:focus-visible {
  outline: 3px solid #f1c40f;
  outline-offset: -3px;
  border-radius: 10px;
}

h3 {
  margin: 0;
  color: #fff;
  font-size: clamp(1rem, 1vw + 0.6rem, 1.15rem);
  line-height: 1.4;
}

p {
  margin: 0;
  padding: 0 20px 20px;
  color: #e8e8e8;
  font-size: clamp(0.95rem, 1vw + 0.5rem, 1.05rem);
  line-height: 1.7;
}

@media (max-width: 600px) {
  .faq-block {
    padding: 24px 16px;
  }

  summary {
    padding: 16px;
  }

  p {
    padding: 0 16px 16px;
  }
}
</style>
